import React, {useState, useEffect} from 'react';
import {
  Grid,
  Card,
  IconButton,
  Tooltip,
  Box,
  TextField,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Avatar,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
} from '@mui/material';
import {
  Search,
  Add,
  Delete,
  Edit,
  ContentCopy,
  Visibility,
  MoreVert,
  FilterList,
  LibraryAdd,
  Download,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import {Axios} from 'services/axios';
import {Block, Confirm} from 'notiflix';
import {useNavigate} from 'react-router-dom';
import {Controller, useForm} from 'react-hook-form';

function SectionLibrary() {
  const navigate = useNavigate();

  const [libraryItems, setLibraryItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: {errors},
  } = useForm({
    defaultValues: {
      name: '',
      templateId: '',
      tags: [],
      content: {},
      settings: {},
    },
  });

  useEffect(() => {
    fetchLibraryItems();
    fetchTemplates();
  }, [search, selectedTags]);

  useEffect(() => {
    if (loading) {
      Block.circle('#library-container', 'Loading Library...');
    } else {
      Block.remove('#library-container');
    }
  }, [loading]);

  const fetchLibraryItems = async () => {
    try {
      setLoading(true);
      const params = {search};
      if (selectedTags.length > 0) {
        params.tags = selectedTags.join(',');
      }

      const res = await Axios.fetch('section-library', {params});
      setLibraryItems(res.data.data.items || []);

      // Extract all unique tags
      const tags = new Set();
      res.data.data.items.forEach(item => {
        item.tags?.forEach(tag => tags.add(tag));
      });
      setAllTags(Array.from(tags));
    } catch (error) {
      console.error('Error fetching library:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await Axios.fetch('section-templates', {
        params: {isActive: true, limit: 100},
      });
      setTemplates(res.data.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({open: true, message, severity});
  };

  const handleMenuOpen = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedItem(null);
  };

  // ========== ADD TO LIBRARY ==========
  const handleAddToLibrary = () => {
    reset({
      name: '',
      templateId: '',
      tags: [],
      content: {},
      settings: {},
    });
    setAddDialog(true);
  };

  const onSubmitAdd = async data => {
    try {
      // console.log('data : ', data);
      Block.circle('#add-dialog', 'Saving to library...');

      await Axios.post('section-library', {
        name: data.name,
        templateId: data.templateId,
        tags: data.tags || [],
      });

      Block.remove('#add-dialog');
      setAddDialog(false);
      fetchLibraryItems();
      showSnackbar('Section saved to library');
    } catch (error) {
      console.error('Error saving to library:', error);
      Block.remove('#add-dialog');
      showSnackbar('Error saving to library', 'error');
    }
  };

  // ========== EDIT LIBRARY ITEM ==========
  const handleEdit = item => {
    setSelectedItem(item);
    reset({
      name: item.name,
      templateId: item.templateId,
      tags: item.tags || [],
    });
    setEditDialog(true);
    handleMenuClose();
  };

  const onSubmitEdit = async data => {
    try {
      Block.circle('#edit-dialog', 'Updating...');

      await Axios.put(`section-library/${selectedItem.id}`, {
        name: data.name,
        tags: data.tags || [],
      });

      Block.remove('#edit-dialog');
      setEditDialog(false);
      fetchLibraryItems();
      showSnackbar('Library item updated');
    } catch (error) {
      console.error('Error updating:', error);
      Block.remove('#edit-dialog');
      showSnackbar('Error updating', 'error');
    }
  };

  // ========== DELETE LIBRARY ITEM ==========
  const handleDelete = () => {
    Confirm.show(
      'Delete Library Item',
      `Are you sure you want to delete "${selectedItem.name}"?`,
      'Yes, Delete',
      'Cancel',
      async () => {
        try {
          Block.circle('#library-container');
          await Axios.delete(
            `section-library/${selectedItem.id}?label=${encodeURIComponent(selectedItem.name)}`,
          );
          fetchLibraryItems();
          showSnackbar('Item deleted');
        } catch (error) {
          console.error('Error deleting:', error);
          showSnackbar('Error deleting', 'error');
        } finally {
          Block.remove('#library-container');
          handleMenuClose();
        }
      },
    );
  };

  // ========== USE ON PAGE ==========
  const handleUseOnPage = () => {
    const pageId = prompt('Enter Page ID to use this section on:');
    if (pageId) {
      applyPageSection(pageId);
    }
    handleMenuClose();
  };

  const applyPageSection = async pageId => {
    try {
      Block.circle('#library-container');
      await Axios.post(`section-library/${selectedItem.id}/use`, {
        pageId,
      });
      showSnackbar('Section added to page');
    } catch (error) {
      console.error('Error using on page:', error);
      showSnackbar('Error adding to page', 'error');
    } finally {
      Block.remove('#library-container');
    }
  };

  // ========== PREVIEW ==========
  const handlePreview = () => {
    // Navigate to preview page or show modal
    alert('Preview feature coming soon');
    handleMenuClose();
  };

  // ========== DUPLICATE ==========
  const handleDuplicate = async () => {
    try {
      Block.circle('#library-container');
      const newName = prompt(
        'Enter name for duplicate:',
        `${selectedItem.name} (Copy)`,
      );
      if (newName) {
        await Axios.post(`section-library/${selectedItem.id}/duplicate`, {
          newName,
        });
        fetchLibraryItems();
        showSnackbar('Item duplicated');
      }
    } catch (error) {
      console.error('Error duplicating:', error);
    } finally {
      Block.remove('#library-container');
      handleMenuClose();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3} id="library-container">
        <Grid container spacing={3}>
          {/* Header */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info">
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="h6" color="white">
                      Section Library
                    </MDTypography>
                    <MDTypography variant="body2" color="white" opacity={0.8}>
                      Reusable section templates
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search library..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      sx={{bgcolor: 'white', borderRadius: 1}}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} sx={{textAlign: 'right'}}>
                    <MDButton
                      variant="gradient"
                      color="dark"
                      startIcon={<Add />}
                      onClick={handleAddToLibrary}>
                      Add to Library
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Tags Filter */}
              <MDBox p={3}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <FilterList color="action" />
                  <MDTypography variant="body2">Filter by tags:</MDTypography>
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Chip
                    label="All"
                    onClick={() => setSelectedTags([])}
                    color={selectedTags.length === 0 ? 'primary' : 'default'}
                    variant={selectedTags.length === 0 ? 'filled' : 'outlined'}
                  />
                  {allTags.map(tag => (
                    <Chip
                      key={tag}
                      label={tag}
                      onClick={() => {
                        if (selectedTags.includes(tag)) {
                          setSelectedTags(selectedTags.filter(t => t !== tag));
                        } else {
                          setSelectedTags([...selectedTags, tag]);
                        }
                      }}
                      color={selectedTags.includes(tag) ? 'primary' : 'default'}
                      variant={
                        selectedTags.includes(tag) ? 'filled' : 'outlined'
                      }
                    />
                  ))}
                </Box>
              </MDBox>

              {/* Library Items Grid */}
              <MDBox p={3}>
                <Grid container spacing={2}>
                  {libraryItems.length === 0 ? (
                    <Grid item xs={12}>
                      <Paper sx={{p: 4, textAlign: 'center'}}>
                        <MDTypography color="text">
                          No items in library. Click "Add to Library" to save
                          your first section.
                        </MDTypography>
                      </Paper>
                    </Grid>
                  ) : (
                    libraryItems.map(item => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                        <Card sx={{height: '100%', position: 'relative'}}>
                          {/* Template Thumbnail */}
                          <MDBox
                            sx={{
                              height: 120,
                              bgcolor: 'grey.100',
                              backgroundImage: item.template?.thumbnailUrl
                                ? `url(${item.template.thumbnailUrl})`
                                : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                            }}>
                            {!item.template?.thumbnailUrl && (
                              <Avatar
                                sx={{
                                  width: 50,
                                  height: 50,
                                  bgcolor: 'primary.main',
                                }}>
                                {item.name.charAt(0)}
                              </Avatar>
                            )}
                            <IconButton
                              size="small"
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'white',
                              }}
                              onClick={e => handleMenuOpen(e, item)}>
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </MDBox>

                          {/* Item Info */}
                          <MDBox p={2}>
                            <MDTypography
                              variant="body2"
                              fontWeight="bold"
                              noWrap>
                              {item.name}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text"
                              display="block">
                              Template: {item.template?.name}
                            </MDTypography>

                            {/* Tags */}
                            <Box
                              display="flex"
                              gap={0.5}
                              flexWrap="wrap"
                              mt={1}>
                              {item.tags?.map((tag, idx) => (
                                <Chip
                                  key={idx}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                            </Box>

                            {/* Stats */}
                            <Box
                              display="flex"
                              justifyContent="space-between"
                              mt={2}>
                              <MDTypography variant="caption" color="text">
                                Used: {item.usageCount || 0} times
                              </MDTypography>
                              <MDTypography variant="caption" color="text">
                                Created:{' '}
                                {new Date(item.createdAt).toLocaleDateString()}
                              </MDTypography>
                            </Box>
                          </MDBox>
                        </Card>
                      </Grid>
                    ))
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}>
        <MenuItem onClick={handlePreview}>
          <Visibility fontSize="small" sx={{mr: 1}} /> Preview
        </MenuItem>
        <MenuItem onClick={() => handleEdit(selectedItem)}>
          <Edit fontSize="small" sx={{mr: 1}} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDuplicate}>
          <ContentCopy fontSize="small" sx={{mr: 1}} /> Duplicate
        </MenuItem>
        <MenuItem onClick={handleUseOnPage}>
          <LibraryAdd fontSize="small" sx={{mr: 1}} /> Use on Page
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{color: 'error.main'}}>
          <Delete fontSize="small" sx={{mr: 1}} /> Delete
        </MenuItem>
      </Menu>

      {/* Add to Library Dialog */}
      <Dialog
        open={addDialog}
        onClose={() => setAddDialog(false)}
        maxWidth="sm"
        fullWidth
        id="add-dialog">
        <DialogTitle>Add to Library</DialogTitle>
        <form onSubmit={handleSubmit(onSubmitAdd)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Section Name"
                  {...register('name', {required: 'Name is required'})}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Template</InputLabel>
                  <Select
                    label="Template"
                    {...register('templateId', {
                      required: 'Template is required',
                    })}
                    error={!!errors.templateId}>
                    {templates.map(template => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name} ({template.typeKey})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="tags"
                  control={control}
                  defaultValue={[]}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <Autocomplete
                      multiple
                      freeSolo
                      options={allTags}
                      value={value || []}
                      onChange={(e, newValue) => {
                        onChange(newValue);
                        // console.log('Tags updated:', newValue);
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Tags"
                          placeholder="Add tags and press Enter"
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option}
                            size="small"
                            {...getTagProps({index})}
                          />
                        ))
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Save to Library
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="sm"
        fullWidth
        id="edit-dialog">
        <DialogTitle>Edit Library Item</DialogTitle>
        <form onSubmit={handleSubmit(onSubmitEdit)}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Section Name"
                  {...register('name', {required: 'Name is required'})}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={allTags}
                  value={watch('tags') || []}
                  onChange={(e, newValue) => setValue('tags', newValue)}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label="Tags"
                      placeholder="Add tags and press Enter"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        size="small"
                        {...getTagProps({index})}
                      />
                    ))
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Update
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default SectionLibrary;
